import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import SmartTable from '../../components/common/table-component';
import DashboardHeader from '../../components/header/DashboardHeader';
import MobileMenu from '../../components/header/MobileMenu';
import DashboardEmployerSidebar from '../../components/header/DashboardSidebar';
import CopyrightFooter from '../../components/footer/employers/CopyrightFooter';
import BackPrevious from '../../components/back-previous';
import { DocumentPreviewUI } from '../../components/document-preview';
import { callGetCommissionDetails } from '../../api/contract/service';
import { convertStringToJson, decodeBase64, FormatCurrencys } from '../../utils/commonUtil';
import colors from '../../utils/colors';
import './commission-ui-only.css';
import { getCommissionAgentScope } from './commissionAgentScope';

const emptyData = {
    result: [],
    totalRecords: 0,
    totalPages: 1,
    sumRecords: 0,
};

const CommissionPayoutViewPage = () => {
    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.dataLogin);
    const agentScope = getCommissionAgentScope(userInfo);
    const isAgentUser = agentScope.isAgentUser;
    const loggedAgentId = agentScope.agentId;
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(emptyData);

    const payout = useMemo(() => {
        try {
            const decoded = decodeBase64(params.id);
            return decoded ? JSON.parse(decoded) : null;
        } catch (error) {
            console.error('Invalid commission payout detail params:', error);
            return null;
        }
    }, [params.id]);

    const isPayoutAllowed = !isAgentUser
        || (
            Boolean(loggedAgentId)
            && Boolean(payout?.agentId)
            && String(payout.agentId) === String(loggedAgentId)
        );

    const columns = [
        {
            label: 'agentFullName',
            key: 'agentFullName',
            style: { minWidth: 180 },
        },
        {
            label: 'agentCode',
            key: 'agentCode',
            style: { minWidth: 110 },
        },
        {
            label: 'custFullName',
            key: 'custFullName',
            style: { minWidth: 180 },
        },
        {
            label: 'contractNumber',
            key: 'contractNumber',
            style: { minWidth: 130 },
        },
        {
            label: 'billNumber',
            key: 'billNumber',
            style: { minWidth: 150 },
        },
        {
            label: 'commissionAmount',
            key: 'commissionAmount',
            className: 'text-end',
            style: { minWidth: 130 },
            render: (value) => (
                <span style={{ color: colors.primary, fontWeight: 700 }}>
                    {Number(value || 0).toLocaleString()}
                </span>
            ),
        },
        {
            label: 'companyReceiveTotal',
            key: 'companyReceiveTotal',
            className: 'text-end',
            style: { minWidth: 140 },
            render: (value) => (
                <span style={{ color: colors.primary, fontWeight: 700 }}>
                    {Number(value || 0).toLocaleString()}
                </span>
            ),
        },
        {
            label: 'earnedDate',
            key: 'earnedDate',
            style: { minWidth: 155 },
            render: (value) => value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
        },
        {
            label: 'status',
            key: 'status',
            style: { minWidth: 120 },
            render: (value) => {
                if (Number(value) === 0) return <span className="badge bg-warning text-dark">{t('status.eearned')}</span>;
                if (Number(value) === 1) return <span className="badge bg-info">{t('status.approved')}</span>;
                if (Number(value) === 2) return <span className="badge bg-secondary">{t('status.inPayout')}</span>;
                if (Number(value) === 3) return <span className="badge bg-success">{t('status.paid')}</span>;
                if (Number(value) === 4) return <span className="badge bg-danger">{t('status.cancelled')}</span>;
                return <span className="badge bg-light text-dark">{value ?? '-'}</span>;
            },
        },
    ];

    const loadData = async (lazyParams = {}) => {
        if (!payout?.id || !isPayoutAllowed) return;
        if (isAgentUser && !loggedAgentId) return;

        try {
            setLoading(true);
            const request = {
                page: 1,
                maxResult: 10,
                keyword: '',
                payoutId: payout.id,
                agentId: isAgentUser ? loggedAgentId : (payout.agentId || null),
                payout: 2,
                ...lazyParams,
                ...(isAgentUser ? { agentId: loggedAgentId } : {}),
            };

            const rsp = await dispatch(callGetCommissionDetails(request));
            if (rsp?.payload?.responseCode === '00000') {
                setData(rsp.payload.data || emptyData);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payout?.id, isPayoutAllowed, isAgentUser, loggedAgentId]);

    const payoutStatus = () => {
        if (Number(payout?.status) === 1) return <span className="badge bg-secondary">{t('status.inPayout')}</span>;
        if (Number(payout?.status) === 2) return <span className="badge bg-success">{t('status.paid')}</span>;
        if (Number(payout?.status) === 3) return <span className="badge bg-danger">{t('status.cancelled')}</span>;
        return <span className="badge bg-light text-dark">{payout?.status ?? '-'}</span>;
    };

    const paymentMethodLabel = () => {
        if (!payout?.paymentMethod) return '-';
        if (payout.paymentMethod === 'TRANSFER') return t('commission.transfer');
        if (payout.paymentMethod === 'CASH') return t('commission.cash');
        return payout.paymentMethod;
    };

    const renderSlip = () => {
        if (!payout?.slipPath) return <span className="commission-empty-value">-</span>;
        try {
            return <DocumentPreviewUI documents={convertStringToJson(payout.slipPath)} variant="cell" />;
        } catch (error) {
            return <span className="commission-empty-value">-</span>;
        }
    };

    return (
        <div className="page-wrapper dashboard commission-ui-only commission-payout-view">
            <span className="header-span"></span>
            <DashboardHeader />
            <MobileMenu />
            <DashboardEmployerSidebar />

            <section className="user-dashboard">
                <div className="dashboard-outer">
                    <div className="upper-title-box back-title-group commission-page-title">
                        <BackPrevious />
                        <div>
                            <h4>{t('commission.payoutDetailTitle')}</h4>
                            <div className="commission-page-subtitle">{t('commission.payoutDetailDescription')}</div>
                        </div>
                    </div>

                    {!payout?.id || !isPayoutAllowed ? (
                        <div className="ls-widget commission-card">
                            <div className="widget-content p-4 text-center text-muted">
                                {t('commission.invalidPayout')}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="ls-widget commission-card commission-detail-hero">
                                <div className="widget-content p-4">
                                    <div className="commission-detail-hero-head">
                                        <div>
                                            <div className="commission-eyebrow">{t('commission.payoutNo')}</div>
                                            <div className="commission-payout-number">{payout.payoutNo || '-'}</div>
                                        </div>
                                        <div>{payoutStatus()}</div>
                                    </div>

                                    <div className="row g-3 mt-1">
                                        <div className="col-xl-3 col-md-6">
                                            <div className="commission-detail-field">
                                                <span>{t('commission.agent')}</span>
                                                <strong>{payout.agentCode + " - " + payout.agentFullName || '-'}</strong>
                                            </div>
                                        </div>
                                        <div className="col-xl-3 col-md-6">
                                            <div className="commission-detail-field">
                                                <span>{t('commission.paymentMethod')}</span>
                                                <strong>{paymentMethodLabel()}</strong>
                                            </div>
                                        </div>
                                        <div className="col-xl-3 col-md-6">
                                            <div className="commission-detail-field">
                                                <span>{t('commission.paidDate')}</span>
                                                <strong>{payout.paidDate ? moment(payout.paidDate).utc().format('DD/MM/YYYY HH:mm') : '-'}</strong>
                                            </div>
                                        </div>
                                        <div className="col-xl-3 col-md-6">
                                            <div className="commission-detail-field">
                                                <span>{t('commission.countContract')}</span>
                                                <strong>{payout.totalItems ?? data.totalRecords ?? 0}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-lg-6">
                                    <div className="card h-100 commission-stat-card commission-money-card">
                                        <div className="card-body">
                                            <div className="text-muted mb-1">{t('commission.totalCommission')}</div>
                                            <div className="commission-money-value">{FormatCurrencys(payout.totalCommission || 0)}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="card h-100 commission-stat-card commission-money-card">
                                        <div className="card-body">
                                            <div className="text-muted mb-1">{t('commission.companyReceiveTotal')}</div>
                                            <div className="commission-money-value">{FormatCurrencys(payout.companyReceiveTotal || 0)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="ls-widget commission-card">
                                <div className="widget-content p-4">
                                    <div className="commission-section-title">
                                        <div>
                                            <h5>{t('commission.payoutInformation')}</h5>
                                            <p>{t('commission.payoutInformationDescription')}</p>
                                        </div>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-lg-6">
                                            <div className="commission-info-row">
                                                <span>{t('commission.note')}</span>
                                                <strong>{payout.note || '-'}</strong>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="commission-info-row commission-info-document">
                                                <span>{t('commission.imagePath')}</span>
                                                <div>{renderSlip()}</div>
                                            </div>
                                        </div>
                                        {Number(payout.status) === 3 && (
                                            <div className="col-12">
                                                <div className="commission-reject-box">
                                                    <span>{t('commission.reversedReason')}</span>
                                                    <strong>{payout.reversedReason || '-'}</strong>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="ls-widget commission-card">
                                <div className="widget-content p-4">
                                    <div className="commission-section-title">
                                        <div>
                                            <h5>{t('commission.commissionList')}</h5>
                                            <p>{t('commission.payoutItemsDescription')}</p>
                                        </div>
                                        <span className="commission-record-count">
                                            {data.totalRecords || 0} {t('commission.items')}
                                        </span>
                                    </div>

                                    <SmartTable
                                        showSumRecord={false}
                                        showConfirmButton={false}
                                        columns={columns}
                                        data={data}
                                        loading={loading}
                                        onLazyLoad={(lazyParams) => loadData(lazyParams)}
                                        recordsTableFilter={true}
                                        customPagination={true}
                                        selectable={false}
                                        summaryColumns={['commissionAmount', 'companyReceiveTotal']}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            <CopyrightFooter />
        </div>
    );
};

export default CommissionPayoutViewPage;
